import { Controller, useForm } from 'react-hook-form';
import { Column, Row } from 'Components/Grid';
import { editCompanyProfile, getCompanyBySlug } from 'services/company-service';
import { Container } from 'Components/Container';
import { Card } from 'Components/Card';
import { ButtonForm, CustomTextField } from 'Components/Form/styles';
import { FormControl, TextField } from '@mui/material';
import { useQuery } from 'react-query';
import { Shimmer } from 'Components/PageLoader/styles';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { MuiChipsInput } from 'mui-chips-input';
import { PageHelper } from '../../Components/PageHelper';

/**
 * LAYER 1 OF PROTECTION - Frontend Whitelist
 *
 * Only these fields can be edited via the profile page
 * Protected fields (CNPJ, signedPlan, roles, managers, etc.) are NOT sent to backend
 */
const EDITABLE_FIELDS = [
  'name',
  'corporateName',
  'description',
  'size',
  'income',
  'industry',
  'domains',
];

export const CompanyProfile = () => {
  const SwalReact = withReactContent(Swal);
  const selectedTenant = useSelector(useAuthTenant);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
  } = useForm({
    defaultValues: {
      active: true,
      cnpj: '',
      corporateName: '',
      description: '',
      domains: [],
      income: '',
      industry: '',
      managers: [],
      name: '',
      partner: false,
      roles: [],
      size: '',
      slug: '',
    },
  });

  /**
   * LAYER 1A: Whitelist - Only send allowed fields
   * Filters the form values to include ONLY editable fields
   */
  const formmatValues = values => {
    const filtered = {};
    EDITABLE_FIELDS.forEach(field => {
      if (values[field] !== undefined) {
        filtered[field] = values[field] ?? '';
      }
    });
    return filtered;
  };

  /**
   * LAYER 1: Frontend Protection
   * Uses editCompanyProfile endpoint instead of editCompany
   * Only sends whitelisted fields to the backend
   */
  const handleComplete = async () => {
    try {
      const res = await editCompanyProfile(selectedTenant._id, formmatValues(getValues()));
      if (res.status === 200) {
        SwalReact.fire({
          title: 'Sucesso!',
          text: 'Empresa atualizada com sucesso!',
          icon: 'success',
        });
      } else {
        throw new Error('Ocorreu um erro ao tentar editar a Empresa!');
      }
    } catch (error) {
      SwalReact.fire({
        title: 'Erro!',
        text: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };
  const { isLoading, isSuccess, isError } = useQuery(['getCompanyBySlug'], async () => {
    if (selectedTenant.slug === undefined) return null;
    const result = await getCompanyBySlug(selectedTenant.slug);
    if (result?.data) {
      Object.keys(result?.data).forEach(key => {
        setValue(key, result?.data[key]);
      });
    }
    return result?.data;
  });

  // Configuração dos passos do tour interativo
  const tourSteps = [
    {
      target: 'body',
      content: 'Bem-vindo ao Perfil da Empresa! Aqui você pode editar as informações cadastrais da sua empresa. Vamos conhecer os campos principais!',
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="page-title"]',
      content: 'Clique aqui no título para navegar e acessar outras seções do sistema.',
      placement: 'bottom',
      title: '↩️ Navegação pelo Título',
    },
    {
      target: '[data-tour="company-info"]',
      content: 'Nesta seção você pode atualizar as informações básicas da empresa como nome fantasia e razão social.',
      placement: 'bottom',
      title: '📝 Informações Básicas',
    },
    {
      target: '[data-tour="legal-info"]',
      content: 'Aqui ficam os dados legais e de porte da empresa: CNPJ e quantidade de funcionários.',
      placement: 'bottom',
      title: '🏢 Dados Legais',
    },
    {
      target: '[data-tour="business-info"]',
      content: 'Configure informações sobre o negócio como receita anual e setor de atuação (indústria).',
      placement: 'bottom',
      title: '💼 Dados do Negócio',
    },
    {
      target: '[data-tour="domains"]',
      content: 'Adicione os domínios de e-mail da sua empresa. Digite o domínio e pressione Enter para adicionar.',
      placement: 'bottom',
      title: '🌐 Domínios',
    },
    {
      target: '[data-tour="description"]',
      content: 'Use este campo para adicionar uma descrição ou resumo sobre a empresa.',
      placement: 'top',
      title: '📄 Descrição',
    },
    {
      target: '[data-tour="save-button"]',
      content: 'Após fazer as alterações, clique aqui para salvar as mudanças no perfil da empresa.',
      placement: 'top',
      title: '💾 Salvar Alterações',
    },
    {
      target: 'body',
      content: 'Pronto! Agora você sabe como editar o perfil da empresa. Você pode reativar este tour clicando no botão de ajuda (?) quando precisar.',
      placement: 'center',
      title: '✨ Tour Concluído!',
    },
  ];

  return (
    <Container title="Perfil da empresa" breadcrumb="Gerenciar / Perfil da empresa" linkPage="#">
      {/* Helper Interativo */}
      {isSuccess && (
        <PageHelper 
          steps={tourSteps}
          tourKey="company-profile-tour"
          autoStart={true}
          showButton={true}
        />
      )}
      
      <form onSubmit={handleSubmit(handleComplete)} className="w-100 mt-3">
        <Row alignItems="start" justifyContent="center">
          <Column xs={12}>
            <Card>
              {isLoading && !isError && (
                <>
                  <Row>
                    <Column xs={12}>
                      <Shimmer style={{ height: '48px' }}></Shimmer>
                    </Column>
                  </Row>
                  {[1, 2, 3, 4, 5].map((_, index) => (
                    <Row key={`shimer-${index}`}>
                      <Column xs={12}>
                        <Shimmer style={{ marginTop: '16px', height: '48px' }}></Shimmer>
                      </Column>
                    </Row>
                  ))}
                </>
              )}
              {isSuccess && (
                <>
                  <div data-tour="company-info">
                    <Row>
                      <Column xs={12} xl={6}>
                        <CustomTextField
                          {...register('name', { required: 'Nome é obrigatório' })}
                          label="Nome da empresa"
                        autoFocus
                        error={errors?.name ? true : false}
                        sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                        helperText={errors?.name ? errors.name.message : null}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Column>
                    <Column xs={12} xl={6}>
                      <CustomTextField
                        {...register('corporateName', { required: 'Razão Social é obrigatório' })}
                        label="Razão social"
                        autoFocus
                        error={errors?.corporateName ? true : false}
                        sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                        helperText={errors?.corporateName ? errors.corporateName.message : null}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Column>
                  </Row>
                  </div>
                  <div data-tour="legal-info">
                  <Row>
                    <Column xs={12} xl={6}>
                      <CustomTextField
                        {...register('cnpj', { required: 'CNPJ é obrigatório' })}
                        label="CNPJ"
                        disabled
                        error={errors?.cnpj ? true : false}
                        sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                        helperText={errors?.cnpj ? errors.cnpj.message : 'Campo não editável (Answer 2-C)'}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Column>
                    <Column xs={12} xl={6}>
                      <CustomTextField
                        {...register('size')}
                        label="Quantidade de funcionários"
                        sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Column>
                  </Row>
                  </div>
                  <div data-tour="business-info">
                  <Row>
                    <Column xs={12} xl={6}>
                      <CustomTextField
                        {...register('income')}
                        label="Receita"
                        sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Column>
                    <Column xs={12} xl={6}>
                      <CustomTextField
                        {...register('industry')}
                        label="Indústria"
                        sx={{ minHeight: '48px', margin: '0 auto 24px', width: '100%' }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Column>
                  </Row>
                  </div>
                  <div data-tour="domains">
                  <Row justifyContent="center">
                    <Column xs={12} className="d-flex">
                      <FormControl fullWidth sx={{ margin: '0 auto 24px' }}>
                        <Controller
                          control={control}
                          name="domains"
                          render={({ field: { onChange, value } }) => (
                            <MuiChipsInput
                              label="Domínios"
                              value={value}
                              onChange={onChange}
                              InputLabelProps={{ shrink: true }}
                              placeholder="Digite e aperte enter"
                              renderChip={(Component, props) => {
                                return (
                                  <Component
                                    {...props}
                                    variant="outlined"
                                    color="primary"
                                    sx={{
                                      width: 'fit-content',
                                    }}
                                  />
                                );
                              }}
                              sx={{
                                '& .MuiButtonBase-root.MuiChip-root.MuiChip-filled': {
                                  width: 'fit-content',
                                },
                                '& .MuiButtonBase-root.MuiIconButton-root': {
                                  width: 'fit-content',
                                  marginTop: '0px',
                                },
                              }}
                            />
                          )}
                        ></Controller>
                      </FormControl>
                    </Column>
                  </Row>
                  </div>
                  <div data-tour="description">
                  <Row>
                    <Column xs={12}>
                      <TextField
                        {...register('description')}
                        label="Resumo"
                        multiline
                        rows={3}
                        sx={{ margin: '0 auto 4px', width: '100%' }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Column>
                  </Row>
                  </div>
                  <div data-tour="save-button">
                  <Row>
                    <Column xs={12} justifyContent="end" flex className="mt-3">
                      <ButtonForm label="Salvar alterações" type="submit" />
                    </Column>
                  </Row>
                  </div>
                </>
              )}
            </Card>
          </Column>
        </Row>
      </form>
    </Container>
  );
};
